import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { config } from '@abyss/web/tools/config';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import { find } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Quality } from '../../../../../api/src/models/ProviderRecommendationDetails';
import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  ProviderTypeMapFF,
  getPremiumCareContent,
} from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { phoneOnly } from '../../../common/ConstantsStyles';
import { getGeoLocationFromStorage } from '../../../common/PSXHeader/SearchBar/utils';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { getLanguage } from '../../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useLagoon } from '../../../hooks/useLagoon';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import {
  RecommendationCriteriaMatch,
  getMatchDescription,
  isSuppressPremiumSmartchoice,
} from '../../../utils/smartChoice.utils';
import { adobeModalTrackEvent } from '../../AdobeTagging/adobeModalTrackEvent';
import { getFeatureFlag } from '../../Utils';
import { defaultStringToEmpty } from '../../Widget/SmartChoiceWidget/utils';
import { MatchText } from '../MatchText';
import {
  BreakdownContainer,
  ContainerWithTopandBottonBorder,
  CriteriaDescription,
  CriteriaPointsContainer,
  CriteriaTitle,
  DescriptionContainer,
  DrawerContentWithBackgroundgray,
  FAQContainer,
  FAQLink,
  StyledDescription,
} from '../SmartChoiceBreakDown.styles';
import { NotAMatchNoServiceDataTextText } from './Components/NotAMatchNoServiceDataText';
import { NotAMatchPremiumCarePhysicianText } from './Components/NotAMatchPremiumCarePhysicianText';
import { ExploreUnaitedHealthPremiumLink } from './QualityBreakDown.styles';

const shouldShowLLMBlock = (
  isWidget: boolean,
  billingCode: string | undefined,
  llmStatus: string | undefined,
  isInvokeLLMModel: boolean,
  llmStatusParam: string | null
): boolean => {
  if (isWidget) {
    return !!billingCode && llmStatus !== 'NA';
  }
  return isInvokeLLMModel && llmStatusParam !== 'NA';
};

export interface QualityBreakDownProps {
  quality: Quality;
  providerType?: string;
  billingCode?: string;
}

export const QualityBreakDown = ({
  quality,
  providerType,
  billingCode,
}: QualityBreakDownProps) => {
  const { portalData } = useContext(PortalContext);
  const isWidget = useStore(StoreKeys?.IS_WIDGET);
  const [searchParams] = useSearchParams();
  const { code: currentSelectedLanguage } = getLanguage();
  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const all_criteria_met = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_ALL_CRITERIA_MET_TITLE_AND_DESCRIPTION,
  });
  const description = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_DESCRIPTION,
  });
  const credentialed_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_CREDENTIALED_PROVIDER,
  });
  const premiumCareData = getPremiumCareContent(currentSelectedLanguage);
  const premium_care_physician = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_PCP_TEXT,
  })?.message;
  const not_premium_care_physician = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_PCP_NEGATIVE_TEXT,
  })?.message;
  const service_data_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_SERVICE_DATA_AVAILABLE,
  });
  const no_service_data_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_NO_SERVICE_DATA_AVAILABLE,
  });
  const service_data_available_text = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_SERVICE_DATA_AVAILABLE_TEXT,
  });
  const no_service_data_available_text = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_NO_SERVICE_DATA_AVAILABLE_TEXT,
  });
  const what_is_premium_care_physician = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_MATCH_WHAT_IS_PRIMARY_CARE_PHYSICIAN_Q_AND_A,
  });
  const not_match_premium_care_physician = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_NOTMATCH_PRIMARY_CARE_PHYSICIAN_TITLE_AND_DESCRIPTION,
  });
  const explore_unaited_health_premium = premiumCareData?.explore;
  const FAQs = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BREAKDOWN_FAQ_TEXT,
  });
  const phoneScreen = useMediaQuery(phoneOnly);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const enableSmartChoiceQualityLlmStatus: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SMART_CHOICE_QUALITY_LLM_STATUS
  );
  const enLink = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .EXPLORE_PREMIUM_LINK_EN,
  });
  const esLink = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .EXPLORE_PREMIUM_LINK_ES,
  });

  const popoverTitle =
    quality?.llmStatus === '1'
      ? service_data_available?.message
      : no_service_data_available?.message;

  const popoverText =
    quality?.llmStatus === '1'
      ? service_data_available_text?.message
      : no_service_data_available_text?.message;

  const serviceDataPopoverContent = (
    <Flex
      css={phoneScreen ? { paddingLeft: '18px', paddingRight: '18px' } : {}}
    >
      <Text color={'#424242'} fontWeight={'400'} size={'14px'}>
        {popoverText}
      </Text>
    </Flex>
  );

  const allConfigs = useLagoon('config')();

  const premiumSuppressionState = allConfigs?.find(
    (row) => row.key === 'SMART_CHOICE_SUPRESS_PREMIUM'
  )?.value;

  const { stateCode } = getGeoLocationFromStorage();
  const isPremiumCareSuppressed = isSuppressPremiumSmartchoice(
    premiumSuppressionState,
    stateCode
  );
  const isUmr = isUmrMember(portalData?.portalName);
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');

  const llmFF = `ENABLE_LLM_${defaultStringToEmpty(
    searchParams?.get('chipValue') ?? ''
  )
    ?.toUpperCase()
    ?.replace(/\s+/g, '_')}_${ProviderTypeMapFF[providerType]}`;

  const isInvokeLLMModel = getFeatureFlag(featureFlags, llmFF);

  useEffect(() => {
    adobeModalTrackEvent('Smart choice quality breakdown');
  }, []);

  return (
    <BreakdownContainer>
      <DrawerContentWithBackgroundgray direction="column">
        <React.Fragment>
          <CriteriaTitle data-testid="match-description">
            {t(
              getMatchDescription(
                quality?.scoreDetails?.match ?? RecommendationCriteriaMatch.None
              )
            )}
          </CriteriaTitle>
          <CriteriaDescription data-testid="criteria-met-description">
            {all_criteria_met?.message}
          </CriteriaDescription>
        </React.Fragment>
      </DrawerContentWithBackgroundgray>
      <DescriptionContainer>
        <StyledDescription data-testid="quality-breakdown-description">
          {description?.message}
        </StyledDescription>
      </DescriptionContainer>
      <CriteriaPointsContainer css={{ marginBottom: '0px' }} direction="column">
        {quality?.credentialed === 'Y' && (
          <MatchText text={credentialed_provider?.message} />
        )}

        {enableSmartChoiceQualityLlmStatus &&
          shouldShowLLMBlock(
            isWidget,
            billingCode,
            quality?.llmStatus,
            isInvokeLLMModel,
            searchParams?.get('llmStatus')
          ) && (
            <Flex>
              {quality?.llmStatus === '1' ? (
                <MatchText text={service_data_available?.message} />
              ) : (
                <NotAMatchNoServiceDataTextText
                  text={no_service_data_available?.message}
                />
              )}
              <Drawer
                isOpen={isDrawerOpen && phoneScreen}
                onClose={() => {
                  adobeLinkTrackEvent({
                    name: 'Service data available',
                    location: 'Smart choice quality breakdown',
                  });
                  setIsDrawerOpen(!isDrawerOpen);
                }}
                position="bottom"
                size="100"
                title={popoverTitle}
              >
                {serviceDataPopoverContent}
              </Drawer>
              {phoneScreen ? (
                <IconSymbol
                  color="$interactive1"
                  css={{ marginLeft: '8px' }}
                  data-testid="quality-breakdown-drawer-icon"
                  icon="info"
                  onClick={() => setIsDrawerOpen(true)}
                  variant="outlined"
                />
              ) : (
                <Popover
                  content={serviceDataPopoverContent}
                  onOpenChange={() => setIsPopoverOpen(!isPopoverOpen)}
                  open={isPopoverOpen}
                  title={popoverTitle}
                >
                  <IconSymbol
                    color="$interactive1"
                    css={{ marginLeft: '8px' }}
                    icon="info"
                    variant="outlined"
                  />
                </Popover>
              )}
            </Flex>
          )}
        {quality?.premiumDesignation !== 'NA' && (
          <>
            {isPremiumCareSuppressed === 'N' && (
              <>
                {quality?.premiumDesignation === 'Y' && (
                  <MatchText
                    StartIcon={
                      <React.Fragment>
                        <span>
                          <IconSymbol color="#002677" icon="favorite" />
                          <IconSymbol color="#002677" icon="favorite" />
                        </span>
                      </React.Fragment>
                    }
                    text={premium_care_physician}
                  />
                )}
                {(quality?.premiumDesignation === 'N' ||
                  quality?.premiumDesignation === '') && (
                  <NotAMatchPremiumCarePhysicianText
                    text={not_premium_care_physician}
                  />
                )}
              </>
            )}
            {isPremiumCareSuppressed === 'N' && (
              <ContainerWithTopandBottonBorder
                data-testid="quality-breakdown-top-and-bottom-border"
                direction="column"
              >
                {quality?.credentialed === 'Y' &&
                  quality?.premiumDesignation === 'Y' && (
                    <React.Fragment>
                      <StyledDescription data-testid="what-is-premium-care-physician-description">
                        {what_is_premium_care_physician?.message}
                      </StyledDescription>
                    </React.Fragment>
                  )}

                {((quality?.credentialed === 'Y' &&
                  quality?.premiumDesignation === 'N') ||
                  (quality?.credentialed === 'Y' &&
                    quality?.premiumDesignation === '') ||
                  (quality?.credentialed === 'N' &&
                    quality?.premiumDesignation === 'Y') ||
                  (quality?.credentialed === 'N' &&
                    quality?.premiumDesignation === '')) && (
                  <React.Fragment>
                    <StyledDescription data-testid="not-match-premium-care-physician-description">
                      {not_match_premium_care_physician?.message}
                    </StyledDescription>
                  </React.Fragment>
                )}

                <ExploreUnaitedHealthPremiumLink
                  data-auto-testid="explore-unaited-health-premium-link"
                  data-testid="explore-unaited-health-premium-link"
                  href={
                    currentSelectedLanguage === 'en' ? enLink?.url : esLink?.url
                  }
                  onClick={() => {
                    adobeLinkTrackEvent({
                      name: 'Explore unitedhealth premium',
                      location: 'Smart choice quality breakdown',
                    });
                  }}
                >
                  {explore_unaited_health_premium}
                </ExploreUnaitedHealthPremiumLink>
              </ContainerWithTopandBottonBorder>
            )}
          </>
        )}
      </CriteriaPointsContainer>

      <FAQContainer>
        <FAQLink
          data-auto-testid="faq-link"
          data-testid="faq-link"
          href={`${baseUrl}/findcare/faq${isWidget ? '?source=cex' : ''}`}
          onClick={() => {
            adobeLinkTrackEvent({
              name: 'Faqs',
              location: 'Smart choice quality breakdown',
            });
          }}
        >
          {FAQs?.message}
        </FAQLink>
      </FAQContainer>
    </BreakdownContainer>
  );
};
